// Which cubes the selected datasets expand to. A pure function over the
// aggregations config (fetched from the Pod via /api/config/aggregations, with
// each cube's dataset filled in), used by the partner flow to show what is
// about to be published before anything is written.
//
// `period` is the global dimension appended to every aggregate. This mirrors
// the server (web/server/publish.js `selectedSlices`) exactly — the backend
// recomputes the same list from the same Pod config when it actually
// publishes, so this copy is for the preview, never the authority on what ships.

import { hidingRule } from '../../../lib/aggregate_core.js';

export function selectedSlices(aggregations, datasetFlags) {
  const period = aggregations.period;
  return aggregations.aggregates
    .filter((a) => datasetFlags[a.dataset])
    .map((a) => ({
      dataSet: a.id,
      dataset: a.dataset,
      dimensions: period ? [...a.dimensions, period] : a.dimensions,
      measure: a.measure ?? aggregations.measure,
      reducer: a.reducer ?? 'count',
    }));
}

// The datasets that have cubes, each with the cubes it groups, in the order
// datasets.json lists them (`datasets` from /api/datasets). Small numbers are
// the dataset's, carried by each of its cubes. Each cube carries its own `sharing`; the group's is that sharing when
// all its cubes agree, and null when they differ (Artist diversity: its region
// cube goes to Sala Apolo only).
export function datasetGroups(aggregations, datasets = []) {
  const period = aggregations.period;
  const byDataset = new Map(datasets.map((d) => [d.id, null]));
  for (const a of aggregations.aggregates) {
    if (!a.dataset) continue;
    if (!byDataset.get(a.dataset)) {
      byDataset.set(a.dataset, {
        id: a.dataset,
        label: datasets.find((d) => d.id === a.dataset)?.label ?? a.dataset,
        suppressBelow: a.suppressBelow,
        rule: hidingRule(a),
        slices: [],
      });
    }
    byDataset.get(a.dataset).slices.push({
      dataSet: a.id,
      dimensions: period ? [...a.dimensions, period] : a.dimensions,
      measure: a.measure ?? aggregations.measure,
      reducer: a.reducer ?? 'count',
      suppressMeasureBelow: a.suppressMeasureBelow ?? null,
      sharing: a.sharing ?? { mode: 'mutual' },
    });
  }
  const same = (x, y) => JSON.stringify(x) === JSON.stringify(y);
  return [...byDataset.values()].filter(Boolean).map((group) => ({ ...group,
    sharing: group.slices.every((s) => same(s.sharing, group.slices[0].sharing)) ? group.slices[0].sharing : null }));
}

// What a cube's (or a group's) sharing intent means, in words.
export const sharingWords = (sharing) => sharing.mode === 'mutual' ? 'shared both ways'
  : (sharing.to ?? []).length ? `given to ${sharing.to.join(', ')}, nothing back` : 'currently private';

// A dataset's name from /api/datasets, or its id when it has none.
export const datasetLabel = (datasets, id) => datasets?.find((d) => d.id === id)?.label ?? id;
